/**
 * A note at the foot of the page its reference landed on.
 *
 * WHY IT WAITED. A footnote is the one piece of a document whose POSITION is not a property of the
 * document: "the foot of this page" means nothing until something has decided where the page ends,
 * and until page setup and page breaks landed, the editor and the renderer did not agree about
 * that. They do now -- one set of numbers in pageGeometry.ts and _page_size, and the preview pushes
 * the same blocks the renderer breaks -- so a note can be put where both of them will put it.
 *
 * THE MARKER IS AN ATOM, THE NOTE IS AN ATTRIBUTE ON IT. ProseMirror's own footnote example gives
 * the node inline CONTENT and edits it in a nested editor. We do not, for a reason specific to a
 * controlled document: content is in the reading order. The note's words would come back from
 * every text extraction we have -- the search index, the digest's plain text, the thumbnail, a
 * screen reader -- in the middle of the sentence that cites them. An attribute is read where it is
 * asked for and nowhere else.
 *
 * WHAT THAT COSTS, and it is worth stating: an attribute is replaced whole, so two people editing
 * the SAME note in the same second end with one of the two, where two people editing the same
 * paragraph end with both. Notes are short and are written by whoever is writing the sentence; the
 * paragraph the note hangs off keeps its character-by-character merge.
 *
 * THE NUMBER IS NOT STORED. It is the count of the notes before it, which is a fact about the
 * document rather than about the note -- see footnotes.ts, and heading numbers for the same rule
 * applied to the same problem.
 */
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { paginationKey } from "./pagination";

export const Footnote = Node.create({
    name: "footnote",
    group: "inline",
    inline: true,
    atom: true,
    // SELECTABLE so it can be clicked and deleted. Deleting the marker deletes the note, which is
    // right: a note with nothing citing it is not a footnote, it is a stranded sentence at the
    // bottom of a page.
    selectable: true,

    addAttributes() {
        return {
            text: {
                default: "",
                parseHTML: (element) => element.getAttribute("data-footnote") || "",
                renderHTML: (attributes) => ({ "data-footnote": attributes.text || "" }),
            },
        };
    },

    parseHTML() {
        return [{ tag: "sup[data-footnote]" }];
    },

    renderHTML({ HTMLAttributes }) {
        // NO NUMBER HERE. renderHTML runs for one node and knows nothing about the rest of the
        // document, so a number drawn here would not notice a note being inserted above it.
        // FootnoteNumbers fills it in, in one pass, after every change -- exactly as the
        // cross-references do, and for exactly the same reason.
        return ["sup", mergeAttributes(HTMLAttributes, { class: "parkour-footnote" })];
    },

    addCommands() {
        return {
            insertFootnote: (text: string = "") => ({ commands }: any) =>
                commands.insertContent({ type: "footnote", attrs: { text } }),
            setFootnoteText: (position: number, text: string) => ({ tr, dispatch }: any) => {
                const node = tr.doc.nodeAt(position);
                if (!node || node.type.name !== "footnote") return false;
                if (dispatch) tr.setNodeMarkup(position, undefined, { ...node.attrs, text });
                return true;
            },
        } as any;
    },
});

const key = new PluginKey("footnoteNumbers");

/**
 * Put 1, 2, 3 on the markers, in document order, and let CSS draw them.
 *
 * ONE PASS OVER THE WHOLE DOCUMENT, because what a marker reads as depends on how many notes are
 * ahead of it: add one in the first paragraph and every marker below changes, though none of their
 * own nodes did.
 *
 * `data-text` AND GENERATED CONTENT, never a DOM write. Writing the number into the sup's
 * textContent mutates the editable DOM behind ProseMirror's back; its observer reads that as
 * somebody typing, dispatches a transaction, and the next pass mutates again. That loop cost an
 * afternoon on the cross-references and is not worth repeating.
 */
export const FootnoteNumbers = Extension.create({
    name: "footnoteNumbers",

    addProseMirrorPlugins() {
        return [new Plugin({
            key,
            props: {
                decorations(state) {
                    const decorations: Decoration[] = [];
                    const how = state.doc.attrs?.footnoteNumbering;
                    // RESTART AT EACH SECTION WHEN THE DOCUMENT ASKS. A section is a TOP-LEVEL
                    // Heading 1 and nothing else -- the same rule the outline uses, and the same
                    // one notesOf applies, because the screen and the PDF must not disagree about
                    // what a marker says.
                    const bySection = how === "section";
                    // AND ON EACH PAGE, which is a different kind of question entirely. Which
                    // SECTION a note is in can be read off the body; which PAGE it is on is a
                    // property of the layout and is only known once pagination has measured. So
                    // this reads the answer out of that plugin's state -- and before the first
                    // measurement arrives the map is empty, which numbers the document straight
                    // through. That is deliberate: 1,1,1,1 for one frame would flicker on open.
                    //
                    // WITH THE PAGE PREVIEW OFF the map is empty too, and it stays empty: there
                    // are no sheets to restart on, so the numbers run straight through and the
                    // guide says so.
                    const pageOf: Map<number, number> = how === "page"
                        ? (paginationKey.getState(state)?.pageOf ?? new Map()) : new Map();

                    let number = 0;
                    let order = 0;
                    let page: number | null = null;
                    const next = (node: any) => {
                        if (how === "page") {
                            const sheet = pageOf.has(order) ? (pageOf.get(order) as number) : page;
                            if (page !== null && sheet !== page) number = 0;
                            if (sheet !== null) page = sheet;
                        }
                        order += 1;
                        number += 1;
                        return number;
                    };

                    state.doc.forEach((block: any, offset: number) => {
                        if (bySection && block.type.name === "heading"
                            && Number(block.attrs?.level) === 1) number = 0;
                        block.descendants((node: any, inner: number) => {
                            if (node.type.name !== "footnote") return;
                            decorations.push(mark(node, next(node), offset + 1 + inner));
                        });
                        if (block.type.name === "footnote") {
                            decorations.push(mark(block, next(block), offset));
                        }
                    });
                    return DecorationSet.create(state.doc, decorations);
                },
            },
        })];
    },
});

/** One marker's decoration: the number it carries, and whether its note has been written. */
function mark(node: any, number: number, position: number) {
    return Decoration.node(position, position + node.nodeSize, {
        "data-text": String(number),
        // AN UNWRITTEN NOTE SAYS SO. A marker whose note is still empty is a sentence with a
        // dangling promise in it, and the author is the only person who can see it before the PDF
        // is made.
        class: (node.attrs?.text || "").trim()
            ? "parkour-footnote" : "parkour-footnote parkour-footnote-empty",
    });
}


